/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useCallback } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import AuthGuardRedirect from "components/Common/AuthGuardRedirect";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

import { touchActivity } from "utils/activity";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import ProtectedRoute from "components/Common/ProtectedRoute";
import { useTranslation } from "react-i18next";
import { getRoleFromStorage } from "utils/auth";

export default function App() {
  const { t } = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const [authKind, setAuthKind] = useState(() => getRoleFromStorage() || "ADMIN");

  useEffect(() => {
    //const syncAuthKind = () => setAuthKind(localStorage.getItem("auth_kind") || "ADMIN");
    const sync = () => setAuthKind(getRoleFromStorage() || "ADMIN");

    // Fires when OTHER tabs update localStorage
    window.addEventListener("storage", sync);
    // Fires when THIS tab logs in/out (we will dispatch it)
    window.addEventListener("authChanged", sync);

    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("authChanged", sync);
    };
  }, []);

  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    const handler = () => touchActivity();

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    touchActivity(); // initialize on load

    return () => events.forEach((e) => window.removeEventListener(e, handler));
  }, []);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  /**
   * Filter routes (and nested collapse routes) by role.
   * If route.roles is missing => show to all.
   */
  const filterRoutesByRole = useCallback((routesList, role) => {
    const filtered = (routesList || [])
      .filter((r) => !r.roles || r.roles.includes(role))
      .map((r) => {
        if (r.collapse && Array.isArray(r.collapse)) {
          return { ...r, collapse: filterRoutesByRole(r.collapse, role) };
        }
        return r;
      });

    // Remove any parent "collapse" that ends up with zero children and has no route itself
    return filtered.filter(
      (r) => !(r.collapse && Array.isArray(r.collapse) && r.collapse.length === 0 && !r.route)
    );
  }, []);

  const filteredRoutes = useMemo(
    () => filterRoutesByRole(routes, authKind),
    [filterRoutesByRole, authKind]
  );

  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      const elements = [];

      if (route.route) {
        const element = route.protected ? (
          <ProtectedRoute roles={route.roles}>{route.component}</ProtectedRoute>
        ) : (
          route.component
        );
        elements.push(<Route exact path={route.route} element={element} key={route.key} />);
      }

      if (route.collapse && Array.isArray(route.collapse) && route.collapse.length > 0) {
        elements.push(...getRoutes(route.collapse));
      }

      return elements;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName={t("app.brandName")}
              routes={filteredRoutes} // ✅ filtered for sidebar
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(filteredRoutes)} {/* ✅ filtered for route registration */}
          <Route path="/" element={<AuthGuardRedirect />} />
          <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName={t("app.brandName")}
            routes={filteredRoutes} // ✅ filtered for sidebar
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(filteredRoutes)} {/* ✅ filtered for route registration */}
        <Route path="/" element={<AuthGuardRedirect />} />
        <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
      </Routes>
    </ThemeProvider>
  );
}
